import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Search, Clock, Package } from "lucide-react";
import { Item } from "@/utils/dashboardDataUtils";

interface UnifiedProductSelectionGridProps {
  availableItems: Item[];
  selectedItems: Item[];
  onItemSelect: (item: Item) => void;
  loading: boolean;
}

const UnifiedProductSelectionGrid: React.FC<
  UnifiedProductSelectionGridProps
> = ({ availableItems, selectedItems, onItemSelect, loading }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredItems = availableItems.filter((item) =>
    (item.title || item.name || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <p>Loading items...</p>;
  }

  return (
    <Card className="p-4 md:p-6 bg-[#F2F2F2] flex flex-col">
      <h2 className="text-lg font-medium mb-4 flex-shrink-0">
        Select Products and Subscription(s)
      </h2>

      <div className="mb-4 relative flex-shrink-0">
        <div className="relative">
          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-9"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
      </div>

      <div className="space-y-2 pb-4">
        {filteredItems.length === 0 ? (
          <p className="text-sm text-gray-500 col-span-full">
            {searchQuery
              ? "No items matching your search"
              : "No active items available."}
          </p>
        ) : (
          filteredItems.map((item) => {
            const isChecked = !!selectedItems.find((p) => p.id === item.id);
            const isSubscription = item.isSubscription;
            const displayName = item.title || item.name || "";
            const displayAmount = item.amount
              ? `$${Number(item.amount).toFixed(2)}`
              : "";
            const billing_period = item.billing_period || "month";

            return (
              <Card
                key={item.id}
                className="p-3 md:p-4 hover:shadow-md transition-shadow bg-white relative"
              >
                <div className="absolute top-3 right-3">
                  <Checkbox
                    id={`item-${item.id}`}
                    checked={isChecked}
                    onCheckedChange={() => onItemSelect(item)}
                    className="border-2 border-gray-400 data-[state=checked]:border-primary"
                  />
                </div>
                <div className="pr-8">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                      <Label
                        htmlFor={`item-${item.id}`}
                        className="font-medium leading-none"
                      >
                        {displayName}
                      </Label>
                      <span
                        className={`self-start inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors whitespace-nowrap ${
                          isSubscription
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary text-secondary-foreground"
                        }`}
                      >
                        {isSubscription ? (
                          <span className="flex items-center">
                            Subscription
                          </span>
                        ) : (
                          <span className="flex items-center">One-Time</span>
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {displayAmount}
                      {isSubscription && billing_period
                        ? `/${billing_period}`
                        : ""}
                    </p>
                    {item.description && (
                      <div className="text-xs text-gray-500 mt-1 max-h-12 overflow-y-auto overflow-x-hidden scrollbar-hide">
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </Card>
  );
};

export default UnifiedProductSelectionGrid;